import { useEffect, useState, useMemo, useRef } from "react";
import useWebSocket from "react-use-websocket";
import {
  games as gameQuerys,
  courses as courseQuerys,
} from "@nualang/nualang-api-and-queries/Queries";
import {
  shuffle,
  removeAllSymbols,
  localeCompare,
  removeExtraWhiteSpaces,
} from "@nualang/nualang-ui-components/dist/utils/index";
import {
  createGame,
  createGameMember,
  deleteGameMember,
  getClassroomGames,
  updateGame,
  getGameMembers,
  getGame,
  updateGameMember,
} from "@nualang/nualang-api-and-queries/APIs/Games";
import { getCourseSectionTopic } from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopics";
import { getCourse } from "@nualang/nualang-api-and-queries/APIs/Courses";
import { getCourseSectionTopicPhraseList } from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopicPhraseLists";
import {
  classrooms as classroomHelpers,
  courses as courseHelpers,
} from "../utils/helpers";
import {
  DataType,
  invalidateCache,
} from "@nualang/nualang-api-and-queries/APIs/invalidation";
import { getIdToken } from "../utils/userAuth";
import config from "../config";
import useDebounce from "@nualang/nualang-ui-components/dist/hooks/useDebounce";
import toast, { useToasterStore } from "react-hot-toast";
import { useTheme } from "@mui/material/styles";
import logger from "../logger";

const ENABLE_REFETCHING = false;
const REFETCHING_FREQUENCY = 10000;

// localStorage key holding the current question's start time (epoch ms),
// persisted so a mid-question refresh cannot reset the scoring baseline.
const TIMER_START_STORAGE_KEY = "timerStart";

const useClassroomLiveGame = ({
  startLoading,
  stopLoading,
  openSnackbar,
  authData,
  isNualangLiveEnabled,
  classroomId,
  navigate,
  gameId,
  username,
  isCreator,
  correctSound,
  incorrectSound,
  tadaSound,
  isClassroomCreator,
  context,
  isIconsVisible,
  selectedRoleplay,
  preferred_username,
  phraseListId: propPhraseListId,
  phrases: propPhrases,
}) => {
  // State
  const [createdGame, setCreatedGame] = useState();
  const [currentRound, setCurrentRound] = useState(0);
  const previousRoundRef = useRef(-1);
  const pendingPhraseListIdRef = useRef(null);
  const pendingPhraseListsRef = useRef(null);
  const pendingPhrasesRef = useRef(null);
  const [isSelectExerciseOpen, setIsSelectExerciseOpen] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(undefined);
  const [currentMessage, setCurrentMessage] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [wordLibrary, setWordLibrary] = useState([]);
  const [wordOptions, setWordOptions] = useState([]);
  const [isAnswered, setIsAnswered] = useState(null);
  const [listeningText, setListeningText] = useState("");
  const [listeningWords, setListeningWords] = useState([]);
  const [translationText, setTranslationText] = useState("");
  const [translationWords, setTranslationWords] = useState([]);
  const [readWriteText, setReadWriteText] = useState("");
  const [useWordBank, setUseWordBank] = useState(false);
  const [isWordBankHidden, setIsWordBankHidden] = useState(false);
  const [answerAttempt, setAnswerAttempt] = useState(null);
  const [isCorrectResultOpen, setIsCorrectResultOpen] = useState(false);
  const [isIncorrectResultOpen, setIsIncorrectResultOpen] = useState(false);
  const [isPartiallyCorrectResultOpen, setIsPartiallyCorrectResultOpen] =
    useState(false);
  const [isTimeUp, setIsTimeUp] = useState(false);
  const [time, setTime] = useState(30);
  const [gameMembersHistory, setGameMembersHistory] = useState([]);
  const [wordBankEnabled, setWordBankEnabled] = useState(true);
  const [keyboardEnabled, setKeyboardEnabled] = useState(true);
  const [hasAnsweredList, setHasAnsweredList] = useState([]);
  const [hasCorrectAnswerList, setHasCorrectAnswerList] = useState([]);
  const [selectedAnswersList, setSelectedAnswersList] = useState([]);
  const [selectedNoAnswersList, setSelectedNoAnswersList] = useState([]);
  const [answerCounts, setAnswerCounts] = useState([]);
  const [noAnswerCounts, setNoAnswerCounts] = useState([]);
  const [currentAnswer, setCurrentAnswer] = useState("");
  const [timeAnswered, setTimeAnswered] = useState(null);
  const [timerStart, setTimerStart] = useState(null);
  const [isFinalScoreDrawerOpen, setIsFinalScoreDrawerOpen] = useState(false);
  const [isRewardEarnedOpen, setIsRewardEarnedOpen] = useState(false);
  const [numberOfQuestions, setNumberOfQuestions] = useState(1);
  const [numberOfPhrases, setNumberOfPhrases] = useState(1);
  const [timer, setTimer] = useState(30);
  const [confirmedNumberOfQuestions, setConfirmedNumberOfQuestions] =
    useState(0);
  const [nextStudentMessage, setNextStudentMessage] = useState(false);
  const [isAnswerAttemptCorrect, setIsAnswerAttemptCorrect] = useState(false);
  const [isStudentAnswerDrawerOpen, setIsStudentAnswerDrawerOpen] =
    useState(false);
  const [roleplayAnswer, setRoleplayAnswer] = useState(" ");

  const debouncedNumberOfQuestions = useDebounce(numberOfQuestions, 500);

  const theme = useTheme();

  const { toasts } = useToasterStore();
  const toastLimit = 5;

  useEffect(() => {
    toasts
      .filter((t) => t.visible)
      .filter((_, i) => i >= toastLimit)
      .forEach((t) => toast.dismiss(t.id));
  }, [toasts]);

  useEffect(() => {
    if (isAnswered !== null) {
      if (isAnswered === true) {
        setTimeAnswered(new Date());
      }

      let isCorrect = false;

      const exerciseName = game?.exercise;
      let answerAttempt;
      if (selectedRoleplay) {
        answerAttempt = currentAnswer;
        isCorrect = answerAttempt.correct;
      } else if (exerciseName === "meaning" || exerciseName === "meaning-read-and-write") {
        answerAttempt = typeof currentAnswer === "string" ? currentAnswer : "";
        isCorrect =
          exerciseName === "meaning-read-and-write"
            ? checkIsMeaningCorrect(answerAttempt, currentQuestion)
            : answerAttempt !== "" && answerAttempt === currentQuestion?.phrase;
      } else {
        answerAttempt =
          useWordBank || !keyboardEnabled
            ? (currentAnswer || []).map((w) => w.text).join(" ")
            : (currentAnswer || "").trim();

        if (exerciseName === "translation" && answerAttempt) {
          const translations = currentQuestion?.translations || [];
          isCorrect = checkIsTranslationCorrect(answerAttempt, translations);
        } else if (exerciseName === "listening" && answerAttempt) {
          isCorrect = checkIsListeningCorrect(answerAttempt, currentQuestion);
        }
      }

      if (exerciseName?.includes("roleplay")) {
        sendJsonMessage({
          action: "MESSAGE",
          payload: {
            classroomId,
            gameId,
            username,
            isAnswered,
            isCorrect,
            message: "ANSWER_STATUS_UPDATE",
            selectedAnswer: answerAttempt.text,
          },
        });
      } else {
        sendJsonMessage({
          action: "MESSAGE",
          payload: {
            classroomId,
            gameId,
            username,
            isAnswered,
            isCorrect,
            message: "ANSWER_STATUS_UPDATE",
          },
        });
      }
    }
  }, [isAnswered, currentAnswer]);

  useEffect(() => {
    if (!isCreator && currentMessage?.question && !isTimeUp) {
      setIsStudentAnswerDrawerOpen(true);
    }
  }, [isCreator, currentMessage, isTimeUp]);

  useEffect(() => {
    const savedIsTimeUp = localStorage.getItem("isTimeUp");
    if (savedIsTimeUp !== null) {
      setIsTimeUp(JSON.parse(savedIsTimeUp));
    }

    const savedTranslationText = localStorage.getItem("translationText");
    if (savedTranslationText !== null) {
      setTranslationText(JSON.parse(savedTranslationText));
    }

    const savedListeningText = localStorage.getItem("listeningText");
    if (savedListeningText !== null) {
      setListeningText(JSON.parse(savedListeningText));
    }

    const savedIsCorrectResultOpen = localStorage.getItem(
      "isCorrectResultOpen",
    );
    if (savedIsCorrectResultOpen !== null) {
      setIsCorrectResultOpen(JSON.parse(savedIsCorrectResultOpen));
    }

    const savedIsPartiallyCorrectResultOpen = localStorage.getItem(
      "isPartiallyCorrectResultOpen",
    );
    if (savedIsPartiallyCorrectResultOpen !== null) {
      setIsPartiallyCorrectResultOpen(
        JSON.parse(savedIsPartiallyCorrectResultOpen),
      );
    }

    const savedIsIncorrectResultOpen = localStorage.getItem(
      "isIncorrectResultOpen",
    );
    if (savedIsIncorrectResultOpen !== null) {
      setIsIncorrectResultOpen(JSON.parse(savedIsIncorrectResultOpen));
    }

    const savedIsStudentAnswerDrawerOpen = localStorage.getItem(
      "isStudentAnswerDrawerOpen",
    );
    if (savedIsStudentAnswerDrawerOpen !== null) {
      setIsStudentAnswerDrawerOpen(JSON.parse(savedIsStudentAnswerDrawerOpen));
    }

    const savedAnswerAttempt = localStorage.getItem("answerAttempt");
    if (savedAnswerAttempt !== null) {
      const parsedAnswerAttempt = JSON.parse(savedAnswerAttempt);
      setAnswerAttempt(parsedAnswerAttempt);
    }

    const savedRoleplayAnswer = localStorage.getItem("roleplayAnswer");
    if (savedRoleplayAnswer !== null) {
      const parsedRoleplayAnswer = JSON.parse(savedRoleplayAnswer);
      setRoleplayAnswer(parsedRoleplayAnswer);
    }

    const savedPhraseAttempt = localStorage.getItem("phraseAttempt");
    if (savedPhraseAttempt !== null) {
      setCurrentAnswer(savedPhraseAttempt);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      "isCorrectResultOpen",
      JSON.stringify(isCorrectResultOpen),
    );
    localStorage.setItem(
      "isPartiallyCorrectResultOpen",
      JSON.stringify(isPartiallyCorrectResultOpen),
    );
    localStorage.setItem(
      "isIncorrectResultOpen",
      JSON.stringify(isIncorrectResultOpen),
    );
    localStorage.setItem(
      "isStudentAnswerDrawerOpen",
      JSON.stringify(isStudentAnswerDrawerOpen),
    );
  }, [
    isCorrectResultOpen,
    isPartiallyCorrectResultOpen,
    isIncorrectResultOpen,
    isStudentAnswerDrawerOpen,
  ]);

  const clearPreviousAttempt = () => {
    setListeningText("");
    setListeningWords([]);
    setTranslationText("");
    setTranslationWords([]);
    setReadWriteText("");
    setWordOptions([]);
    setIsAnswered(false);
    setAnswerAttempt(null);
    setIsAnswerAttemptCorrect(false);
    setIsCorrectResultOpen(false);
    setIsIncorrectResultOpen(false);
    setIsPartiallyCorrectResultOpen(false);
    setHasAnsweredList([]);
    setHasCorrectAnswerList([]);
    setSelectedAnswersList([]);
    setSelectedNoAnswersList([]);
    setAnswerCounts([]);
    setNoAnswerCounts([]);
    setCurrentAnswer(keyboardEnabled ? "" : []);
    setUseWordBank(false);
  };

  const handleReset = () => {
    clearPreviousAttempt();
    setCurrentRound(0);
    previousRoundRef.current = -1;
    handleClearLocalStorage();
  };

  const handleStoreLocalStorageVariables = (
    isTimeUp,
    answerAttempt,
    phraseAttempt,
  ) => {
    if (answerAttempt) {
      localStorage.setItem("answerAttempt", JSON.stringify(answerAttempt));
    }
    if (phraseAttempt) {
      localStorage.setItem("phraseAttempt", JSON.stringify(phraseAttempt));
    }

    if (isTimeUp) {
      localStorage.setItem("isTimeUp", JSON.stringify(isTimeUp));
    }

    if (translationText) {
      localStorage.setItem("translationText", JSON.stringify(translationText));
    }

    if (listeningText) {
      localStorage.setItem("listeningText", JSON.stringify(listeningText));
    }
    if (currentAnswer) {
      localStorage.setItem("currentAnswer", JSON.stringify(currentAnswer));
    }
  };

  // A per-question key that survives a refresh (server-persisted
  // remainingQuestions re-fetch identically) but changes on a genuine new
  // question (its length strictly decreases each round).
  const getQuestionTimerKey = () => {
    if (!gameId || typeof game?.remainingQuestions?.length !== "number") {
      return null;
    }
    return `${gameId}-${game.remainingQuestions.length}`;
  };

  const persistTimerStart = (key, value) => {
    if (!key) {
      return;
    }
    try {
      localStorage.setItem(
        TIMER_START_STORAGE_KEY,
        JSON.stringify({ key, value }),
      );
    } catch (error) {
      logger.error(error);
    }
  };

  // Restore the current question's start time if we already have one persisted
  // for this key (i.e. a mid-question refresh); otherwise seed a fresh one.
  // Returns the resolved start time (epoch ms).
  const restoreOrInitialiseTimerStart = (key) => {
    if (!key) {
      const now = Date.now();
      setTimerStart(now);
      return now;
    }

    try {
      const stored = localStorage.getItem(TIMER_START_STORAGE_KEY);
      if (stored !== null) {
        const parsed = JSON.parse(stored);
        if (parsed?.key === key && Number.isFinite(parsed.value)) {
          setTimerStart(parsed.value);
          return parsed.value;
        }
      }
    } catch (error) {
      logger.error(error);
    }

    const now = Date.now();
    setTimerStart(now);
    persistTimerStart(key, now);
    return now;
  };

  const handleClearLocalStorage = () => {
    localStorage.removeItem("isCorrectResultOpen");
    localStorage.removeItem("isPartiallyCorrectResultOpen");
    localStorage.removeItem("isIncorrectResultOpen");
    localStorage.removeItem("isStudentAnswerDrawerOpen");
    localStorage.removeItem("isCorrect");
    localStorage.removeItem("isTimeUp");
    localStorage.removeItem("answerAttempt");
    localStorage.removeItem("roleplayAnswer");
    localStorage.removeItem("phraseAttempt");
    localStorage.removeItem("translationAttempt");
    localStorage.removeItem("translationText");
    localStorage.removeItem("listeningText");
    localStorage.removeItem("currentAnswer");
  };

  // Websocket
  const [socketUrl, setSocketUrl] = useState(null);

  const handleSetSocketURL = async (currentGameId) => {
    try {
      if (classroomId && currentGameId && username && isNualangLiveEnabled) {
        const authToken = await getIdToken();
        const WS_URL = `${config.WS_API}/games/?classroomId=${classroomId}&gameId=${currentGameId}&username=${username}&Authorization=${authToken}&isCreator=${isCreator}`;
        if (WS_URL !== socketUrl) {
          setSocketUrl(WS_URL);
        }
      }
    } catch (error) {
      logger.error(error);
    }
  };

  useEffect(() => {
    let currentGameId;
    if (gameId) {
      currentGameId = gameId;
    } else if (createdGame && createdGame.gameId) {
      currentGameId = createdGame.gameId;
    }
    handleSetSocketURL(currentGameId);
  }, [
    classroomId,
    gameId,
    username,
    createdGame?.gameId,
    isNualangLiveEnabled,
  ]);

  const handleSetWordBankEnabled = (enabled) => {
    if (enabled === true) {
      sendJsonMessage({
        action: "MESSAGE",
        payload: {
          classroomId,
          gameId,
          username,
          message: "WORD_BANK_ENABLED",
        },
      });
    } else {
      sendJsonMessage({
        action: "MESSAGE",
        payload: {
          classroomId,
          gameId,
          username,
          message: "WORD_BANK_DISABLED",
        },
      });
    }
  };

  const handleSetTimer = (time) => {
    if (time < 10) {
      time = 10;
    } else if (time > 60) {
      time = 60;
    }
    setTimer(time);
  };

  const handleShowFinalScoreDrawer = async () => {
    await invalidateCache(DataType.GAME_MEMBERS, {
      classroomId: classroomId,
      gameId: gameId,
    });
    if (gameMembersQuery) {
      await gameMembersQuery.refetch();
    }
    setIsFinalScoreDrawerOpen(true);
    sendJsonMessage({
      action: "MESSAGE",
      payload: {
        classroomId,
        gameId,
        username,
        message: "UPDATE_GAME_AWARD",
      },
    });
    setTimeout(() => {
      setIsRewardEarnedOpen(true);
    }, 5000);
  };

  const { sendJsonMessage } = useWebSocket(socketUrl, {
    onOpen: () => {
      logger.info("WebSocket connection established.");
    },
    onClose: () => {
      logger.info("WebSocket connection closed.");
      if (context === "play-game") {
        const isCreator =
          username && game?.createdBy === username ? true : false;
        if (!isCreator && game?.gameStatus === "LIVE") {
          if (classroomGamesQuery) {
            classroomGamesQuery.refetch();
          }
          handleReset();
        }
        if (isCreator && game?.gameStatus === "LIVE") {
          handleEndGame(() => {
            if (classroomGamesQuery) {
              classroomGamesQuery.refetch();
            }
            navigate(`/classrooms/${classroomId}`);
            handleReset();
          });
        }
      }
    },
    onMessage: (e) => {
      logger.info(`WebSocket connection message: ${e}`);
      const data = e.data ? JSON.parse(e.data) : {};
      if (data.action === "MESSAGE") {
        const isCreator =
          username && game?.createdBy === username ? true : false;
        if (["JOINED_GAME", "LEFT_GAME"].includes(data.payload.message)) {
          const joined = ["JOINED_GAME"].includes(data.payload.message);
          toast(
            `${data.payload.preferred_username} ${joined ? "joined" : "left"} the game.`,
            {
              style: {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.mode === "dark" ? "#000000" : "#FFFFFF",
              },
            },
          );
          if (gameMembersQuery && isCreator) {
            gameMembersQuery.refetch();
          }
        } else if (data.payload.message === "UPDATE_GAME") {
          if (gameQuery) {
            gameQuery.refetch();
          }
          if (classroomGamesQuery) {
            classroomGamesQuery.refetch();
          }
        } else if (data.payload.message === "TIME_UP") {
          setIsTimeUp(true);
        } else if (data.payload.message === "NEXT_QUESTION") {
          if (gameQuery) {
            gameQuery.refetch();
          }
          handleReset();
          setIsTimeUp(false);
        } else if (data.payload.message === "END_GAME") {
          if (isCreator && gameQuery) {
            gameQuery.refetch();
          }
          if (classroomGamesQuery) {
            classroomGamesQuery.refetch();
          }
          if (!isCreator) {
            if (game?.remainingQuestions?.length !== 1) {
              navigate(`/classrooms/${classroomId}`);
              handleReset();
            }
          }
        } else if (data.payload.message === "SHOW_FINAL_SCORES") {
          if (
            !isCreator &&
            ((game.remainingQuestions.length === 1 &&
              numberOfQuestions !== 1) ||
              game.exercise.includes("roleplay"))
          ) {
            setIsIncorrectResultOpen(false);
            setIsCorrectResultOpen(false);
            handleShowFinalScoreDrawer();
          }
        } else if (data.payload.message === "KEYBOARD_AND_WB") {
          setKeyboardEnabled(true);
          setWordBankEnabled(true);
        } else if (data.payload.message === "KEYBOARD_ONLY") {
          setKeyboardEnabled(true);
          setWordBankEnabled(false);
        } else if (data.payload.message === "WB_ONLY") {
          setKeyboardEnabled(false);
          setWordBankEnabled(true);
          setCurrentAnswer([]);
        } else if (data.payload.message === "UPDATE_TIME") {
          setTime(data.payload.time);
        } else if (data.payload.message === "CANCEL_GAME") {
          if (isCreator && gameQuery) {
            gameQuery.refetch();
          }
          if (classroomGamesQuery) {
            classroomGamesQuery.refetch();
          }
          if (!isCreator) {
            navigate(`/classrooms/${classroomId}`);
            handleReset();
          }
        } else if (data.payload.message === "ANSWER_STATUS_UPDATE") {
          if (data.payload.isAnswered === true && isCreator) {
            if (!hasAnsweredList.includes(data.payload.username)) {
              setHasAnsweredList((prevList) => [
                ...prevList,
                data.payload.username,
              ]);
            }
            if (data.payload.isCorrect === true) {
              if (!hasCorrectAnswerList.includes(data.payload.username)) {
                setHasCorrectAnswerList((prevList) => [
                  ...prevList,
                  data.payload.username,
                ]);
              }
            } else if (data.payload.isCorrect === false) {
              if (hasCorrectAnswerList.includes(data.payload.username)) {
                setHasCorrectAnswerList((prevList) =>
                  (prevList || []).filter(
                    (uId) => uId !== data.payload.username,
                  ),
                );
              }
            }
            if (data.payload.selectedAnswer) {
              setSelectedAnswersList((prevList) => {
                const updatedList = prevList.filter(
                  (answer) => answer.username !== data.payload.username,
                );

                const newList = [
                  ...updatedList,
                  {
                    username: data.payload.username,
                    answer: data.payload.selectedAnswer,
                  },
                ];

                const counts =
                  currentMessage?.answers?.reduce((acc, answerObj) => {
                    acc[answerObj.text] = 0;
                    return acc;
                  }, {}) || {};

                newList.forEach((item) => {
                  if (counts.hasOwnProperty(item.answer)) {
                    counts[item.answer] += 1;
                  }
                });

                setAnswerCounts(counts);
                return newList;
              });
            }
            if (!data.payload.selectedAnswer) {
              setSelectedNoAnswersList((prevList) => {
                const updatedNoAnswerList = prevList.filter(
                  (item) => item.username !== data.payload.username,
                );

                const newNoAnswerList = [
                  ...updatedNoAnswerList,
                  {
                    username: data.payload.username,
                    answer: null,
                  },
                ];
                setNoAnswerCounts(newNoAnswerList.length);

                return newNoAnswerList;
              });
            }
          }
        } else if (data.payload.message === "NEXT_MESSAGE") {
          setNextStudentMessage(!nextStudentMessage);
          setIsTimeUp(false);
          handleReset();
          if (isCreator) {
            handleRemoveExercise();
          }
        }
      }
    },
    onError: (err) => {
      logger.error(`WebSocket connection error: ${err}`);
    },
    shouldReconnect: () => true,
    reconnectAttempts: 3,
    /* 
      attemptNumber will be 0 the first time it attempts to reconnect, 
      so this equation results in a reconnect pattern of 1 second, 2 seconds, 4 seconds, 8 seconds, 
      and then caps at 10 seconds until the maximum number of attempts is reached
    */
    reconnectInterval: (attemptNumber) =>
      Math.min(Math.pow(2, attemptNumber) * 1000, 10000),
  });

  const handleChangeAnswerOptions = (option) => {
    setAnswerOptions(option);
  };

  // Querys
  const classroomGamesQuery = gameQuerys.useGames(
    async (classroomId, filters) => {
      const games = await getClassroomGames(classroomId, false, filters);
      return games.Items;
    },
    {
      classroomId,
      filters: {
        limit: config.limits.games,
        liveOrCreatedOnly: true,
      },
    },
    {
      enabled: !!classroomId && isNualangLiveEnabled,
      staleTime: 500000,
      refetchInterval: (data) => {
        return data?.state?.dataUpdateCount < 30 &&
          !isClassroomCreator &&
          context === "view-classroom" &&
          !createdGame?.gameId
          ? REFETCHING_FREQUENCY
          : false;
      },
    },
  );

  const classroomGames = useMemo(
    () =>
      classroomGamesQuery.isSuccess &&
      Array.isArray(classroomGamesQuery.data) &&
      classroomGamesQuery.data.length
        ? classroomGamesQuery.data
        : [],
    [classroomGamesQuery.data, classroomGamesQuery.isSuccess],
  );

  const gameQuery = gameQuerys.useGame(
    async () => {
      const game = await getGame(classroomId, gameId);
      return game?.Item;
    },
    {
      gameId,
    },
    {
      enabled: !!gameId && !!classroomId && isNualangLiveEnabled,
      refetchInterval: ENABLE_REFETCHING ? REFETCHING_FREQUENCY : false,
    },
  );

  const game = gameQuery.isSuccess && gameQuery.data ? gameQuery.data : {};
  const [answerOptions, setAnswerOptions] = useState(0);

  useEffect(() => {
    if (game.answerOption) {
      setAnswerOptions(game.answerOption);
    }
    if (game.answerOption === "0") {
      setKeyboardEnabled(true);
      setWordBankEnabled(true);
    } else if (game.answerOption === "1") {
      setKeyboardEnabled(true);
      setWordBankEnabled(false);
    } else if (game.answerOption === "2") {
      setKeyboardEnabled(false);
      setWordBankEnabled(true);
      setCurrentAnswer([]);
    }
  }, [game.answerOption]);

  useEffect(() => {
    if (game.timer) {
      setTimer(game.timer);
      setTime(game.timer);
    }
  }, [game.timer]);

  useEffect(() => {
    if (game.numberOfQuestions) {
      setConfirmedNumberOfQuestions(game.numberOfQuestions);
      setNumberOfQuestions(game.numberOfQuestions);
    }
  }, [game.numberOfQuestions]);

  const gameMembersQuery = gameQuerys.useGameMembers(
    async (gameId, filters) => {
      const members = await getGameMembers(classroomId, gameId, false, filters);
      const formattedGameMembers = await classroomHelpers.formatMembers(
        members.Items || [],
        true,
      );

      formattedGameMembers.sort((a, b) => b.score - a.score);

      const formattedGameMembersWithIndex = formattedGameMembers.map(
        (member, index) => ({
          ...member,
          index: index + 1,
        }),
      );

      if (previousRoundRef.current !== currentRound) {
        setGameMembersHistory((prevHistory) => [
          ...prevHistory,
          formattedGameMembersWithIndex,
        ]);
        previousRoundRef.current = currentRound;
      }
      return formattedGameMembers;
    },
    {
      gameId,
      filters: {
        limit: config.limits.gameMembers,
      },
    },
    {
      enabled: !!gameId && isNualangLiveEnabled,
      refetchInterval: ENABLE_REFETCHING ? REFETCHING_FREQUENCY : false,
      keepPreviousData: true,
    },
  );

  const gameMembers = useMemo(
    () =>
      gameMembersQuery.isSuccess &&
      Array.isArray(gameMembersQuery.data) &&
      gameMembersQuery.data.length
        ? gameMembersQuery.data
        : [],
    [gameMembersQuery.data, gameMembersQuery.isSuccess],
  );

  const gameMemberIds = gameMembers.map((m) => m.memberId);

  let courseId, sectionId, topicId;
  if (game?.courseSectionTopicId) {
    const splitIds = game.courseSectionTopicId.split("|");
    courseId = splitIds[0];
    sectionId = splitIds[1];
    topicId = splitIds[2];
  }

  const topicQuery = courseQuerys.useTopic(
    async (cId, sId, tId) => {
      const topic = await getCourseSectionTopic(cId, sId, tId);
      const formattedTopic = await courseHelpers.formatTopic(
        topic.Item,
        authData,
      );
      return formattedTopic;
    },
    {
      courseId,
      sectionId,
      topicId,
      username,
    },
    { enabled: !!courseId && !!sectionId && !!topicId && isNualangLiveEnabled },
  );

  const courseSectionTopic =
    topicQuery.isSuccess && topicQuery.data && topicQuery.data.topicId
      ? topicQuery.data
      : {};

  const courseQuery = courseQuerys.useCourse(
    getCourse,
    { courseId, username },
    { enabled: !!courseId && isNualangLiveEnabled },
  );

  const courseName = courseQuery.isSuccess && courseQuery.data?.Item?.courseName
    ? courseQuery.data.Item.courseName
    : undefined;

  const effectivePhraseListId = propPhraseListId || game?.phraseListId;

  const phraseListQuery = courseQuerys.useCourseSectionTopicPhraseList(
    async (cId, sId, tId, plId, filters) => {
      const res = await getCourseSectionTopicPhraseList(cId, sId, tId, plId, filters);
      const item = res?.Item || null;
      if (item?.phrases) {
        item.phrases = [...item.phrases].sort((a, b) => (a.idx ?? 0) - (b.idx ?? 0));
      }
      return item;
    },
    { courseId, sectionId, topicId, phraseListId: effectivePhraseListId, filters: { includePhrases: "true" } },
    { enabled: !!courseId && !!sectionId && !!topicId && !!effectivePhraseListId && isNualangLiveEnabled },
  );


  const phraseListPhrases =
    effectivePhraseListId &&
    phraseListQuery.isSuccess &&
    Array.isArray(phraseListQuery.data?.phrases)
      ? phraseListQuery.data.phrases
      : null;

  const activePhrases =
    propPhrases?.length > 0
      ? propPhrases
      : phraseListPhrases?.length > 0
        ? phraseListPhrases
        : courseSectionTopic?.phrases;

  const updateGameMemberMutation =
    gameQuerys.useUpdateGameMember(updateGameMember);

  useEffect(() => {
    if (activePhrases) {
      setNumberOfPhrases(activePhrases.length);
      if (!game.numberOfQuestions) {
        // Default the question count to the whole pool; the teacher can lower it
        // via the Game Settings slider.
        setNumberOfQuestions(activePhrases.length);
        setConfirmedNumberOfQuestions(activePhrases.length);
      }
    }
  }, [activePhrases, game?.numberOfQuestions]);

  const handleSetNumberOfQuestions = (e) => {
    setNumberOfQuestions(e.target.value);
  };

  const wsMessageRef = useRef();
  const updateCBRef = useRef();

  const updateGameMutation = gameQuerys.useUpdateGame(updateGame, {
    onSuccess: async () => {
      // openSnackbar('started', 'success');
      sendJsonMessage({
        action: "MESSAGE",
        payload: {
          classroomId,
          gameId,
          username,
          message: wsMessageRef.current,
        },
      });
      if (updateCBRef.current) {
        updateCBRef.current();
      }
    },
    onError: () => {
      openSnackbar("problem", "error");
    },
    onSettled: () => {
      stopLoading();
    },
  });

  // Functions
  const handleUpdateGame = async (values, cb, wsMessage = "UPDATE_GAME") => {
    // startLoading();
    const updateGameData = {
      classroomId,
      gameId,
      values,
    };

    wsMessageRef.current = wsMessage;
    updateCBRef.current = cb;

    updateGameMutation.mutate(updateGameData);
  };

  const handleUpdateGameMemberScore = async (newGameMemberScore, isCorrect) => {
    // startLoading();
    const updateGameMemberData = {
      classroomId,
      gameId,
      memberId: username,
      values: {
        score: newGameMemberScore,
        isCorrect: isCorrect,
      },
    };

    updateGameMemberMutation.mutate(updateGameMemberData);
  };

  // Cap the pool at `cap` phrases while drawing evenly across the source lists
  // (tagged with `sourceListId` when several lists are merged) so one long list
  // can't dominate. Input is already shuffled; the result is reshuffled so the
  // lists interleave rather than play in blocks.
  const drawEvenlyAcrossLists = (phrases, cap) => {
    const groups = new Map();
    phrases.forEach((p) => {
      const key = p?.sourceListId ?? "__single__";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(p);
    });
    const buckets = [...groups.values()];
    // Track a read cursor per bucket so each pick is O(1) (avoids shift()'s O(n)).
    const cursors = buckets.map(() => 0);
    const picked = [];
    let i = 0;
    while (picked.length < cap && buckets.some((b, idx) => cursors[idx] < b.length)) {
      const idx = i % buckets.length;
      const bucket = buckets[idx];
      if (cursors[idx] < bucket.length) picked.push(bucket[cursors[idx]++]);
      i += 1;
    }
    shuffle(picked);
    return picked;
  };

  const handleStartGame = () => {
    handleReset();
    setCurrentRound(currentRound + 1);
    const exerciseName = game?.exercise;
    const phrases = activePhrases;
    let qs = phrases.slice();
    shuffle(qs);
    if (confirmedNumberOfQuestions > 0 && confirmedNumberOfQuestions < qs.length) {
      qs = drawEvenlyAcrossLists(qs, confirmedNumberOfQuestions);
    }
    qs = qs.map((question) => ({
      ...question,
      wordOptions:
        exerciseName === "listening"
          ? listeningWordOptions(question)
          : exerciseName === "meaning" || exerciseName === "meaning-read-and-write"
            ? meaningWordOptions(question, phrases)
            : translationWordOptions(question),
    }));
    if (exerciseName.includes("roleplay")) {
      qs = selectedRoleplay.script;
    }
    handleUpdateGame({
      gameStatus: "LIVE",
      remainingQuestions: qs,
      ...(effectivePhraseListId && { phraseListId: effectivePhraseListId }),
    });
  };

  const handleCancelGame = (cb) => {
    handleUpdateGame(
      {
        gameStatus: "CANCELED",
      },
      cb,
      "CANCEL_GAME",
    );
  };

  const handleEndGame = (cb) => {
    handleReset();
    handleUpdateGame(
      {
        gameStatus: "COMPLETED",
      },
      cb,
      "END_GAME",
    );
    openSnackbar("game_ended", "success");
  };

  const handleConfirm = () => {
    setConfirmedNumberOfQuestions(debouncedNumberOfQuestions);
    sendJsonMessage({
      action: "MESSAGE",
      payload: {
        classroomId,
        gameId,
        username,
        message: "UPDATE_TIME",
        time: timer,
      },
    });
    if (answerOptions === "0") {
      sendJsonMessage({
        action: "MESSAGE",
        payload: {
          classroomId,
          gameId,
          username,
          message: "KEYBOARD_AND_WB",
        },
      });
    } else if (answerOptions === "1") {
      sendJsonMessage({
        action: "MESSAGE",
        payload: {
          classroomId,
          gameId,
          username,
          message: "KEYBOARD_ONLY",
        },
      });
    } else if (answerOptions === "2") {
      sendJsonMessage({
        action: "MESSAGE",
        payload: {
          classroomId,
          gameId,
          username,
          message: "WB_ONLY",
        },
      });
    }
    handleUpdateGame({
      timer: timer,
      answerOption: answerOptions,
      numberOfQuestions: debouncedNumberOfQuestions,
    });
  };

  const createGameMutation = gameQuerys.useCreateGame(createGame, {
    onSettled: () => {
      stopLoading();
    },
    onSuccess: async (data) => {
      const createdGame = data.Item;
      openSnackbar("game_created", "success");
      handleCloseSelectExercise();
      navigate(`/classrooms/${classroomId}/games/${createdGame.gameId}`, {
        state: {
          isGameCreator: true,
          phraseListId: pendingPhraseListIdRef.current,
          phraseLists: pendingPhraseListsRef.current,
          phrases: pendingPhrasesRef.current,
        },
      });
      pendingPhraseListIdRef.current = null;
      pendingPhraseListsRef.current = null;
      pendingPhrasesRef.current = null;
      handleReset();
    },
    onError: () => {
      openSnackbar("problem_creating", "error");
    },
  });

  const handleCreateLiveGame = async (body) => {
    // Accept the multi-list payload (phraseListIds) and normalise the legacy
    // single phraseListId to a one-element list so both callers keep working.
    const listIds =
      Array.isArray(body?.phraseListIds) && body.phraseListIds.length
        ? body.phraseListIds
        : body?.phraseListId
          ? [body.phraseListId]
          : null;
    pendingPhraseListIdRef.current = listIds ? listIds[0] : null;
    pendingPhraseListsRef.current = body?.phraseLists ?? null;
    pendingPhrasesRef.current = body?.phrases ?? null;
    handleReset();
    startLoading();
    return createGameMutation.mutateAsync({ classroomId, body });
  };

  const joinGameMutation = gameQuerys.useJoinGame(createGameMember, {
    onSettled: () => {
      stopLoading();
    },
    onSuccess: async () => {
      sendJsonMessage({
        action: "MESSAGE",
        payload: {
          classroomId,
          gameId,
          username,
          message: "JOINED_GAME",
          preferred_username,
        },
      });
      handleUpdateActiveGameMember(gameId, true);
      if (["CREATED", "LIVE"].includes(game.gameStatus)) {
        openSnackbar("joined_game", "success");
      }
    },
    onError: () => {
      navigate(`/classrooms/${classroomId}`);
      openSnackbar("An error occurred while joining the game", "error");
    },
  });

  const handleJoinLiveGame = async (gameId) => {
    startLoading();
    return joinGameMutation.mutateAsync({ classroomId, gameId });
  };

  const leaveCBRef = useRef();

  const leaveGameMutation = gameQuerys.useLeaveGame(deleteGameMember, {
    onSettled: () => {
      stopLoading();
    },
    onSuccess: async () => {
      sendJsonMessage({
        action: "MESSAGE",
        payload: {
          classroomId,
          gameId,
          username,
          message: "LEFT_GAME",
          preferred_username,
        },
      });
      if (leaveCBRef.current) {
        leaveCBRef.current();
      }
    },
    onError: () => {
      openSnackbar("problem", "error");
    },
  });

  const handleLeaveLiveGame = async (gameId, cb) => {
    startLoading();
    handleReset();
    leaveCBRef.current = cb;
    return leaveGameMutation.mutateAsync({
      classroomId,
      gameId,
      memberId: username,
    });
  };

  const handleUpdateActiveGameMember = async (gameId, isActive) => {
    const updateGameData = {
      classroomId,
      gameId,
      memberId: username,
      values: {
        isActive: isActive,
      },
    };
    if (!isActive) {
      sendJsonMessage({
        action: "MESSAGE",
        payload: {
          classroomId,
          gameId,
          username,
          message: "LEFT_GAME",
          preferred_username,
        },
      });
    }
    await updateGameMemberMutation.mutateAsync(updateGameData);
  };

  const handleShowFinalScores = () => {
    sendJsonMessage({
      action: "MESSAGE",
      payload: {
        classroomId,
        gameId,
        username,
        message: "SHOW_FINAL_SCORES",
      },
    });
  };

  const handleOpenSelectExercise = () => {
    setIsSelectExerciseOpen(true);
  };

  const handleCloseSelectExercise = () => {
    setIsSelectExerciseOpen(false);
  };

  const handleRemoveExercise = () => {
    const updatedRemainingQuestions = game?.remainingQuestions?.slice(1);
    handleUpdateGame({
      remainingQuestions: updatedRemainingQuestions,
    });
  };

  // Effects
  useEffect(() => {
    if (
      context === "view-classroom" &&
      isClassroomCreator &&
      classroomGames.length > 0
    ) {
      if (classroomGames[0].gameStatus !== "LIVE") {
        setCreatedGame(classroomGames[0]);
      }
    } else {
      setCreatedGame(classroomGames[0]); // In the future there should only be one game with this status
    }
  }, [classroomGames, context, isClassroomCreator]);

  useEffect(() => {
    const exerciseName = game?.exercise;
    if (
      ["translation", "listening", "pronunciation", "meaning", "meaning-read-and-write"].includes(exerciseName) &&
      game?.remainingQuestions
    ) {
      const qs = game?.remainingQuestions;
      if (qs && qs.length > 0) {
        setCurrentQuestion(qs[0]);
        setQuestions(qs);
        restoreOrInitialiseTimerStart(getQuestionTimerKey());
        setWordOptions(qs[0].wordOptions);
      }
    }
  }, [game?.remainingQuestions, game?.exercise]);

  useEffect(() => {
    const exerciseName = game?.exercise;
    const phrases = activePhrases;
    if (["translation", "listening"].includes(exerciseName) && phrases) {
      const wordSet = new Set();
      for (const p of phrases) {
        if (exerciseName === "translation") {
          for (const t of p.translations) {
            if (t) {
              for (const w of t.trim().split(" ")) {
                if (w && w !== "") {
                  wordSet.add(w);
                }
              }
            }
          }
        } else if (exerciseName === "listening") {
          for (const p of phrases) {
            for (const w of p.phrase.trim().split(" ")) {
              if (w && w !== "") {
                wordSet.add(w);
              }
            }
          }
        }
      }
      setWordLibrary([...wordSet]);
    }
  }, [activePhrases, game?.exercise]);

  const meaningWordOptions = (question, allPhrases) => {
    const correctPhrase = question.phrase;
    const otherPhrases = allPhrases
      .filter((p) => p.phrase !== correctPhrase)
      .map((p) => p.phrase);
    shuffle(otherPhrases);
    const distractors = otherPhrases.slice(0, 3);
    const options = [
      { text: correctPhrase, id: 0, isCorrect: true },
      ...distractors.map((p, i) => ({ text: p, id: i + 1, isCorrect: false })),
    ];
    shuffle(options);
    return options;
  };

  const translationWordOptions = (question) => {
    const exerciseName = game?.exercise;
    if (["translation"].includes(exerciseName)) {
      if (question?.translations && question?.translations.length > 0) {
        // Create an array of the needed words for the translation
        const currentTranslationArray = question?.translations[0].split(" ");
        const normalizeWord = (word) =>
          word.replace(/[.,!¡¿?;:]/g, "").toLowerCase();
        const normalizedToOriginalMap = new Map();

        currentTranslationArray.forEach((word) => {
          const normalized = normalizeWord(word);
          normalizedToOriginalMap.set(normalized, word);
        });

        // Words that only belong to one of the OTHER accepted translations
        // for this question must never be offered as "incorrect" filler
        // tiles - they're actually correct for a valid alternate
        // translation, which just makes the word bank confusing/misleading.
        const otherTranslationWordsNormalized = new Set(
          question.translations
            .slice(1)
            .flatMap((t) => (t || "").trim().split(" ").filter(Boolean))
            .map(normalizeWord),
        );

        let wordLibraryCopy = [];
        for (let i = 0; i < wordLibrary.length; i++) {
          const word = wordLibrary[i];
          const prevWord = i > 0 ? wordLibrary[i - 1] : null;
          const nextWord =
            i < wordLibrary.length - 1 ? wordLibrary[i + 1] : null;
          const prevPair = prevWord ? `${prevWord}-${word}` : null;
          const nextPair = nextWord ? `${word}-${nextWord}` : null;

          if (
            (prevPair && wordLibrary.includes(prevPair)) ||
            (nextPair && wordLibrary.includes(nextPair))
          ) {
            continue;
          }

          wordLibraryCopy.push(word);
        }

        // Shuffle the word library copy
        shuffle(wordLibraryCopy);

        // Create a set for unique words using normalized words
        const wordSet = new Set();

        // Add normalized currentTranslationArray words to the set
        currentTranslationArray.forEach((word) => {
          const normalized = normalizeWord(word);
          wordSet.add(normalized);
          normalizedToOriginalMap.set(normalized, word); // Store the original word for display
        });

        // Add words from wordLibraryCopy to the set
        wordLibraryCopy.forEach((word) => {
          const normalized = normalizeWord(word);
          if (
            !wordSet.has(normalized) &&
            !otherTranslationWordsNormalized.has(normalized)
          ) {
            wordSet.add(normalized);
            normalizedToOriginalMap.set(normalized, word);
          }
        });

        // Create additional options up to 5 unique words
        const additionalOptions = Array.from(wordSet)
          .slice(
            currentTranslationArray.length,
            currentTranslationArray.length + 5,
          )
          .map((normalized) => normalizedToOriginalMap.get(normalized));

        // Create the word options array using the original formatting for display
        const wordOptions = [
          ...currentTranslationArray,
          ...additionalOptions,
        ].map((text, id) => ({ text, id }));

        shuffle(wordOptions);
        return wordOptions;
      }
      return [];
    }
    return [];
  };

  useEffect(() => {
    if (isIconsVisible && game?.remainingQuestions?.length === 1) {
      if (tadaSound) {
        tadaSound.play();
      }
      handleShowFinalScores();
    }
  }, [isIconsVisible]);

  const listeningWordOptions = (question) => {
    const exerciseName = game?.exercise;
    if (["listening"].includes(exerciseName) && question.phrase) {
      if (wordLibrary && wordLibrary.length > 0) {
        // Create an array of the needed words for the phrase
        const currentQuestionArray = question.phrase.split(" ");
        const normalizeWord = (word) =>
          word.replace(/[.,!¡¿?;:]/g, "").toLowerCase();
        const normalizedToOriginalMap = new Map();

        currentQuestionArray.forEach((word) => {
          const normalized = normalizeWord(word);
          normalizedToOriginalMap.set(normalized, word);
        });

        let wordLibraryCopy = [];
        for (let i = 0; i < wordLibrary.length; i++) {
          const word = wordLibrary[i];
          const prevWord = i > 0 ? wordLibrary[i - 1] : null;
          const nextWord =
            i < wordLibrary.length - 1 ? wordLibrary[i + 1] : null;
          const prevPair = prevWord ? `${prevWord}-${word}` : null;
          const nextPair = nextWord ? `${word}-${nextWord}` : null;

          if (
            (prevPair && wordLibrary.includes(prevPair)) ||
            (nextPair && wordLibrary.includes(nextPair))
          ) {
            continue;
          }
          wordLibraryCopy.push(word);
        }

        // Shuffle the word library copy
        shuffle(wordLibraryCopy);

        // Create a set for unique words using normalized words
        const wordSet = new Set();

        // Add normalized currentQuestionArray words to the set
        currentQuestionArray.forEach((word) => {
          const normalized = normalizeWord(word);
          wordSet.add(normalized);
          normalizedToOriginalMap.set(normalized, word); // Store the original word for display
        });

        // Add words from wordLibraryCopy to the set
        wordLibraryCopy.forEach((word) => {
          const normalized = normalizeWord(word);
          if (!wordSet.has(normalized)) {
            wordSet.add(normalized);
            normalizedToOriginalMap.set(normalized, word);
          }
        });

        // Create additional options up to 5 unique words
        const additionalOptions = Array.from(wordSet)
          .slice(currentQuestionArray.length, currentQuestionArray.length + 5)
          .map((normalized) => normalizedToOriginalMap.get(normalized));

        // Create the word options array using the original formatting for display
        const wordOptions = [...currentQuestionArray, ...additionalOptions].map(
          (text, id) => ({ text, id }),
        );

        shuffle(wordOptions);
        return wordOptions;
      }
    }
    return [];
  };

  const handleNextQuestion = async () => {
    setCurrentRound(currentRound + 1);
    const exerciseName = game?.exercise;
    if (["translation", "listening", "pronunciation", "meaning", "meaning-read-and-write"].includes(exerciseName)) {
      const newQs = game?.remainingQuestions.filter((_, i) => i !== 0);
      setIsTimeUp(false);
      setQuestions(newQs);
      setCurrentQuestion(newQs[0]);
      setHasAnsweredList([]);
      handleUpdateGame(
        {
          remainingQuestions: newQs,
        },
        null,
        "NEXT_QUESTION",
      );
    }
  };

  const handleInputChange = (event) => {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const name = event.target.name;
    if (name === "listeningText") {
      setListeningText(value);
    } else if (name === "translationText") {
      setTranslationText(value);
    } else if (name === "readWriteText") {
      setReadWriteText(value);
    }
  };

  const handleUseWordBank = () => {
    setUseWordBank(true);
    setIsWordBankHidden(false);
    setListeningText("");
    setTranslationText("");
    setReadWriteText("");
    setCurrentAnswer([]);
  };

  const handleAddScore = (
    isCorrect,
    correctWordCounter,
    percentageCorrect,
    correctWordWithAccentsCounter,
    isStory,
  ) => {
    const exerciseName = game?.exercise;
    const scoreToAdd = useWordBank || !keyboardEnabled ? 100 : 150;
    const accentBonus =
      useWordBank || !keyboardEnabled || exerciseName !== "listening"
        ? 0
        : correctWordWithAccentsCounter * 35;

    const finalTimeAnswered = timeAnswered || new Date();
    const difference = Math.min(
      time,
      Math.floor((finalTimeAnswered - timerStart) / 1000),
    );

    const bonus = difference < 5 ? 25 : 0;
    const timeBonus = Math.floor(
      (1 - difference / time / 2) * scoreToAdd + bonus,
    );
    let newScore;
    if (isCorrect) {
      newScore = isStory
        ? timeBonus + scoreToAdd
        : timeBonus + scoreToAdd + accentBonus;
      handleUpdateGameMemberScore(newScore, isCorrect);
    }
    if (isCorrect === false && correctWordCounter >= 1) {
      newScore =
        Math.floor(((timeBonus + scoreToAdd) * percentageCorrect) / 100) +
        accentBonus;
      handleUpdateGameMemberScore(newScore, isCorrect);
    }
    return newScore;
  };

  const checkTranslation = () => {
    let translations;
    if (currentQuestion) {
      translations = currentQuestion?.translations || [];
    } else {
      return;
    }

    const translationAttempt =
      useWordBank || !keyboardEnabled
        ? currentAnswer.map((w) => w.text).join(" ")
        : currentAnswer.trim();

    const isCorrect = checkIsTranslationCorrect(
      translationAttempt,
      translations,
    );

    if (isCorrectResultOpen !== true && isPartiallyCorrectResultOpen !== true) {
      let newScore = handleAddScore(isCorrect);

      const answerAttempt = {
        attempt: translationAttempt,
        answer: translations,
        result: isCorrect ? "correct" : "incorrect",
        method: useWordBank || !keyboardEnabled ? "word_bank" : "text",
        points: newScore > 0 && isCorrect ? newScore : 0,
      };

      handleStoreLocalStorageVariables(isTimeUp, answerAttempt);

      if (isCorrect && isCorrect === true) {
        handleCorrect(answerAttempt);
      } else {
        handleIncorrect(answerAttempt);
      }
    }
  };

  const checkMeaning = () => {
    if (!currentQuestion) return;
    if (isCorrectResultOpen === true || isPartiallyCorrectResultOpen === true) return;
    const correctPhrase = currentQuestion.phrase;
    const exerciseName = game?.exercise;
    let isCorrect;
    if (exerciseName === "meaning-read-and-write") {
      isCorrect = checkIsMeaningCorrect(currentAnswer, currentQuestion);
    } else {
      isCorrect = currentAnswer === correctPhrase;
    }
    let newScore = handleAddScore(isCorrect);
    const answerAttempt = {
      attempt: typeof currentAnswer === "string" ? currentAnswer : "",
      answer: correctPhrase,
      result: isCorrect ? "correct" : "incorrect",
      method: exerciseName === "meaning-read-and-write" ? "text" : "word_bank",
      points: newScore > 0 && isCorrect ? newScore : 0,
    };
    handleStoreLocalStorageVariables(isTimeUp, answerAttempt);
    if (isCorrect) {
      handleCorrect(answerAttempt);
    } else {
      handleIncorrect(answerAttempt);
    }
  };

  const checkStory = (isCorrect, answerAttempt) => {
    if (isCorrectResultOpen !== true && isPartiallyCorrectResultOpen !== true) {
      let newScore = handleAddScore(isCorrect, 0, 0, 0, true);

      answerAttempt = {
        ...answerAttempt,
        points: newScore > 0 && isCorrect ? newScore : 0,
      };
      handleStoreLocalStorageVariables(isTimeUp, answerAttempt);
      localStorage.setItem("roleplayAnswer", JSON.stringify(roleplayAnswer));
      if (isCorrect && isCorrect === true) {
        handleCorrect(answerAttempt);
      } else {
        handleIncorrect(answerAttempt);
      }
    }
  };

  const checkIsMeaningCorrect = (currentAnswer, question) => {
    const attempt = (typeof currentAnswer === "string" ? currentAnswer : "").trim();
    if (attempt === "") return false;
    const matches = (target) =>
      removeAllSymbols(target || "").localeCompare(removeAllSymbols(attempt), undefined, {
        sensitivity: "base",
      }) === 0;
    if (matches(question?.phrase)) return true;
    if (question?.alternativeVersions?.length > 0) {
      return question.alternativeVersions.some((alt) => matches(alt));
    }
    return false;
  };

  const checkIsTranslationCorrect = (currentAnswer, translations) => {
    let translationMatch;
    for (const item of translations) {
      translationMatch = removeAllSymbols(item).localeCompare(
        removeAllSymbols(currentAnswer),
        undefined,
        { sensitivity: "base" },
      );
      if (translationMatch === 0) {
        return true;
      }
    }
  };

  const phraseWordList =
    currentQuestion && currentQuestion.phrase
      ? removeExtraWhiteSpaces(currentQuestion.phrase).split(" ")
      : [];

  const checkListening = () => {
    let correctWordCounter = 0;
    let correctWordWithAccentsCounter = 0;

    const phraseAttempt =
      useWordBank || !keyboardEnabled
        ? currentAnswer.map((w) => w.text).join(" ")
        : currentAnswer.trim();
    const isCorrect = checkIsListeningCorrect(phraseAttempt, currentQuestion);

    (phraseAttempt || "").split(" ").forEach((word, i) => {
      const isWordCorrect =
        phraseWordList[i] &&
        checkIsListeningCorrect(word.trim(), {
          phrase: phraseWordList[i].trim(),
        });

      if (isWordCorrect) {
        correctWordCounter++;
      }
      const isWordWithAccentsMatching =
        phraseWordList[i] &&
        checkIsAccentsMatching(word.trim(), {
          phrase: phraseWordList[i].trim(),
        });

      if (isWordWithAccentsMatching) {
        correctWordWithAccentsCounter++;
      }
    });

    if (isCorrectResultOpen !== true && isPartiallyCorrectResultOpen !== true) {
      const percentageCorrect =
        (correctWordCounter / phraseWordList.length) * 100;

      let newScore = handleAddScore(
        isCorrect,
        correctWordCounter,
        percentageCorrect,
        correctWordWithAccentsCounter,
      );

      const answerAttempt = {
        attempt: phraseAttempt,
        answer: currentQuestion.phrase,
        result: isCorrect
          ? "correct"
          : correctWordCounter >= 1
            ? "partially_correct"
            : "incorrect",
        method: useWordBank || !keyboardEnabled ? "word_bank" : "text",
        points:
          newScore > 0 && (isCorrect || correctWordCounter >= 1) ? newScore : 0,
      };

      handleStoreLocalStorageVariables(isTimeUp, answerAttempt, phraseAttempt);

      if (isCorrect && isCorrect === true) {
        handleCorrect(answerAttempt);
      } else if (correctWordCounter >= 1 && isCorrect === false) {
        handlePartiallyCorrect(answerAttempt);
      } else {
        handleIncorrect(answerAttempt);
      }
    }
  };

  const checkIsListeningCorrect = (currentAnswer, currentPhrase) => {
    if (currentAnswer && currentAnswer) {
      let phraseMatch = localeCompare(
        removeAllSymbols(currentAnswer),
        removeAllSymbols(currentPhrase.phrase),
      );
      if (phraseMatch === 0) {
        return true;
      } else if (
        currentPhrase &&
        currentPhrase.alternativeVersions &&
        currentPhrase.alternativeVersions.length > 0
      ) {
        for (
          let index = 0;
          index < currentPhrase.alternativeVersions.length;
          index++
        ) {
          const alternativeVersion = currentPhrase.alternativeVersions[index];
          phraseMatch = localeCompare(
            removeAllSymbols(currentAnswer),
            removeAllSymbols(alternativeVersion),
          );
          if (phraseMatch === 0) {
            return true;
          }
        }
      }
    }
    return false;
  };

  const checkIsAccentsMatching = (currentAnswer, currentPhrase) => {
    if (
      currentAnswer &&
      currentAnswer.toLowerCase() !==
        removeAllSymbols(
          currentPhrase.phrase
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, ""),
        )
    ) {
      if (
        currentAnswer.toLowerCase().replace(/[,.]$/, "") ===
        currentPhrase.phrase.toLowerCase().replace(/[,.]$/, "")
      ) {
        return true;
      } else if (
        currentPhrase &&
        currentPhrase.alternativeVersions &&
        currentPhrase.alternativeVersions.length > 0
      ) {
        for (
          let index = 0;
          index < currentPhrase.alternativeVersions.length;
          index++
        ) {
          const alternativeVersion = currentPhrase.alternativeVersions[index];
          if (
            currentAnswer &&
            currentAnswer !== removeAllSymbols(alternativeVersion)
          ) {
            if (
              currentAnswer.toLowerCase().replace(/[,.]$/, "") ===
              alternativeVersion.toLowerCase().replace(/[,.]$/, "")
            ) {
              return true;
            }
          } else {
            return false;
          }
        }
      }
    }
    return false;
  };

  const handleCorrect = (answerAttempt) => {
    setAnswerAttempt(answerAttempt);
    if (correctSound) {
      correctSound.play();
    }
    if (!game?.exercise.includes("roleplay")) {
      setIsCorrectResultOpen(true);
    } else {
      setIsAnswerAttemptCorrect(true);
    }
  };

  const handlePartiallyCorrect = (answerAttempt) => {
    if (correctSound) {
      correctSound.play();
    }
    setAnswerAttempt(answerAttempt);
    setIsPartiallyCorrectResultOpen(true);
  };

  const handleIncorrect = (answerAttempt) => {
    setAnswerAttempt(answerAttempt);
    if (incorrectSound) {
      incorrectSound.play();
    }
    if (!game?.exercise.includes("roleplay")) {
      setIsIncorrectResultOpen(true);
    } else {
      setIsAnswerAttemptCorrect(false);
    }
    const isCorrect = false;
    const newScore = 0;
    handleUpdateGameMemberScore(newScore, isCorrect);
  };

  const handleTimeUp = () => {
    setIsTimeUp(true);
    const isCreator = username && game?.createdBy === username ? true : false;
    if (isCreator && game?.gameStatus === "LIVE") {
      sendJsonMessage({
        action: "MESSAGE",
        payload: {
          classroomId,
          gameId,
          username,
          message: "TIME_UP",
        },
      });
    }
  };

  return {
    isSelectExerciseOpen,
    classroomGames,
    createdGame,
    gameMemberIds,
    gameMembers,
    game,
    courseSectionTopic,
    courseName,
    courseQueryIsSuccess: courseQuery.isSuccess,
    activePhrases,
    currentQuestion,
    questions,
    wordLibrary,
    isAnswered,
    useWordBank,
    listeningText,
    listeningWords,
    translationText,
    translationWords,
    readWriteText,
    isWordBankHidden,
    answerAttempt,
    isCorrectResultOpen,
    isIncorrectResultOpen,
    setIsCorrectResultOpen,
    setIsIncorrectResultOpen,
    phraseWordList,
    isPartiallyCorrectResultOpen,
    isTimeUp,
    time,
    wordBankEnabled,
    handleSetWordBankEnabled,
    handleSetTimer,
    setIsAnswered,
    handleStartGame,
    handleCancelGame,
    handleEndGame,
    createGame,
    handleJoinLiveGame,
    handleLeaveLiveGame,
    handleCreateLiveGame,
    handleOpenSelectExercise,
    handleCloseSelectExercise,
    handleRemoveExercise,
    handleNextQuestion,
    handleInputChange,
    handleUseWordBank,
    setListeningWords,
    setTranslationWords,
    setUseWordBank,
    checkListening,
    checkIsListeningCorrect,
    checkTranslation,
    checkMeaning,
    checkIsTranslationCorrect,
    setIsTimeUp,
    handleTimeUp,
    setTime,
    sendJsonMessage,
    gameMembersQuery,
    gameMembersHistory,
    setCurrentAnswer,
    answerOptions,
    setAnswerOptions,
    keyboardEnabled,
    handleChangeAnswerOptions,
    gameMembersAnsweredTotal: hasAnsweredList?.length,
    gameMembersCorrectAnswerTotal: hasCorrectAnswerList?.length,
    selectedAnswersList: selectedAnswersList,
    selectedNoAnswersList: selectedNoAnswersList,
    isFinalScoreDrawerOpen,
    isRewardEarnedOpen,
    setIsRewardEarnedOpen,
    numberOfPhrases,
    numberOfQuestions,
    handleSetNumberOfQuestions,
    handleConfirm,
    confirmedNumberOfQuestions,
    classroomGamesQuery,
    socketUrl,
    checkStory,
    currentAnswer,
    wordOptions,
    setTimerStart,
    restoreOrInitialiseTimerStart,
    currentMessage,
    setCurrentMessage,
    handleUpdateGame,
    gameIsLoading: gameQuery.isLoading,
    isAnswerAttemptCorrect,
    isStudentAnswerDrawerOpen,
    setIsStudentAnswerDrawerOpen,
    roleplayAnswer,
    setRoleplayAnswer,
    handleUpdateActiveGameMember,
    answerCounts,
    noAnswerCounts,
    phraseListImage: phraseListQuery.data?.picture,
    phraseListVoice: phraseListQuery.data?.voice || null,
    phraseListVoiceLanguageCode: phraseListQuery.data?.voiceLanguageCode || null,
    phraseListVoicePitch: phraseListQuery.data?.voicePitch || null,
    phraseListLearnLang: phraseListQuery.data?.learnLang || null,
  };
};
export default useClassroomLiveGame;
